/* eslint-disable security/detect-object-injection -- every index here is a `findIndex` result on a local array,
   never user input. app-ui's eslint config did not flag these; the shared package's does. */
import type { ChatMessage, ChatMode } from '@logz-pkg/models';
import { buildRunMessage } from './chat-content-to-blocks';
import type { IChatContentItem } from './general-agent.types';

const GENERIC_ERROR = 'Something went wrong. Please try again.';
const ASSISTANT_NO_REPLY = 'The assistant finished without a text reply.';

// The in-flight run's transcript, re-mapped to the rich AgentProcess message on each poll tick.
export interface ILiveRun {
  chatContent: IChatContentItem[];
  runId?: string;
  startTimestamp: number;
  // The mode this run was sent in (ORIONIQ-1231) — stamps the live/newest run's ⚡/✨ glyph.
  mode?: ChatMode;
}

const textOr = (value: unknown, fallback: string): string => (typeof value === 'string' && value.trim()) || fallback;

// A successful run with blank/whitespace-only output still needs a visible answer. buildRunMessage emits the
// agentOutput verbatim (an empty/whitespace markdown block, or no block at all), so the consumer substitutes
// the placeholder first — restoring the fallback the removed toAssistantAnswer used to provide.
export const resolveAssistantOutput = (agentOutput?: string | null): string => textOr(agentOutput, ASSISTANT_NO_REPLY);

// `user-<timestamp>` is unique enough: the input is disabled while a turn is in flight, so sends are always
// seconds apart — keeping these mappers pure/deterministic (no crypto/random) for testability.
export const toUserMessage = (message: string, timestamp: number): ChatMessage => ({
  id: `user-${timestamp}`,
  role: 'user',
  timestamp,
  blocks: [{ type: 'text', data: message }],
});

export const toAssistantError = (timestamp: number, message?: string | null): ChatMessage => ({
  id: `error-${timestamp}`,
  role: 'assistant',
  timestamp,
  blocks: [{ type: 'error', data: textOr(message, GENERIC_ERROR) }],
});

// Durable transcript + the in-flight run as one grouped streaming message via the shared buildRunMessage
// (the package groups its thinking/tool_call blocks into AgentProcess). The live message shares the run id
// with its finalized form, so MessageList transitions it streaming→complete in place. Idempotent by run id:
// when the durable transcript ALREADY holds that run (a RESUMED conversation loaded from history carries the
// still-Running run's process-only partial, id = runId), the live stream REPLACES that partial in place rather
// than appending a second copy under the same id (ORIONIQ-1131 away-status v2, bug A). A fresh send / route-away
// resume has no such partial → it appends, exactly as before.
export const composeChatMessages = (durable: ChatMessage[], liveRun: ILiveRun | null): ChatMessage[] => {
  if (!liveRun || liveRun.chatContent.length === 0) return durable;

  const liveMessage = buildRunMessage({
    chatContent: liveRun.chatContent,
    runId: liveRun.runId,
    timestamp: liveRun.startTimestamp,
    status: 'streaming',
    mode: liveRun.mode,
  });
  const index = durable.findIndex(message => message.id === liveMessage.id);

  if (index === -1) return [...durable, liveMessage];

  const next = [...durable];

  // Preserve the loaded partial's stable id + timestamp (the resume's startTimestamp is a fresh origin) + its
  // original mode glyph (the resumed run keeps the mode it was originally sent in, ORIONIQ-1231).
  next[index] = {
    ...liveMessage,
    id: durable[index].id,
    timestamp: durable[index].timestamp,
    mode: durable[index].mode ?? liveMessage.mode,
  };

  return next;
};

// Idempotent commit keyed by message id (else append), preserving the matched message's stable id + timestamp.
// `fallbackId` matches an optimistic stop committed before a backend run id arrived, so the reconcile refines
// that turn in place instead of double-appending.
export const upsertRunMessage = (messages: ChatMessage[], message: ChatMessage, fallbackId?: string): ChatMessage[] => {
  const index = messages.findIndex(
    existing => existing.id === message.id || (fallbackId !== undefined && existing.id === fallbackId),
  );

  if (index === -1) return [...messages, message];

  const next = [...messages];

  next[index] = { ...message, id: messages[index].id, timestamp: messages[index].timestamp };

  return next;
};
